import { useState } from 'react';

import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import UserService from '@/services/user.service';
import SettingsLoadingPage from '@/shared-components/loaders/components/SettingsLoadingPage';
import SettingsHeading from '@/shared-components/text/components/SettingsHeading';

import NotificationPreferenceSection from './components/NotificationPreferenceSection';
import PushDevicePreferenceControl from './components/PushDevicePreferenceControl';
import {
  areAllSectionEventsEnabled,
  getNotificationAudience,
  getNotificationPreferenceSections,
  normalizeNotificationPreferences,
  updateEventPreference,
  updateSectionPreferences
} from './notificationPreferences.config';

import './styles/NotificationPreferences.css';

const PREFERENCES_PATH = 'user_session.user_preferences.notification_preferences';

export default function NotificationsPreferences() {
  const { state, stateHandler } = useStateHandler();
  const t = useTranslations('common.user_settings.communication.notifications');
  const [savingKey, setSavingKey] = useState(null);
  const [saveError, setSaveError] = useState('');
  const userSession = state.user_session;

  if (!userSession) {
    return <SettingsLoadingPage page={t('heading')} />;
  }

  const audience = getNotificationAudience(userSession);
  const preferenceSections = getNotificationPreferenceSections(audience);
  const preferences = normalizeNotificationPreferences(
    userSession.user_preferences?.notification_preferences,
    preferenceSections
  );

  async function savePreferences(nextPreferences, preferenceId) {
    if (savingKey) {
      return;
    }

    const previousPreferences = preferences;

    setSaveError('');
    setSavingKey(preferenceId);
    stateHandler.set(PREFERENCES_PATH, nextPreferences);

    try {
      const { response, data } = await UserService.updateUserPreferences({
        'user_preferences.notification_preferences': nextPreferences
      });
      if (!response?.ok) {
        throw new Error(data?.error_message || t('save_error'));
      }

      const savedPreferences = data?.user_preferences?.notification_preferences;
      if (savedPreferences) {
        stateHandler.set(PREFERENCES_PATH, normalizeNotificationPreferences(savedPreferences, preferenceSections));
      }
    } catch (error) {
      stateHandler.set(PREFERENCES_PATH, previousPreferences);
      setSaveError(error.message || t('save_error'));
    } finally {
      setSavingKey(null);
    }
  }

  function handleSectionChange(section, nextValue) {
    const nextPreferences = updateSectionPreferences(preferences, section, nextValue);
    void savePreferences(nextPreferences, section.id);
  }

  function handleEventChange(section, eventId, nextValue) {
    const nextPreferences = updateEventPreference(preferences, section.id, eventId, nextValue);
    void savePreferences(nextPreferences, `${section.id}.${eventId}`);
  }

  return (
    <>
      <SettingsHeading text={t('heading')} />
      <div className="notification-preferences">
        <p className="notification-preferences__intro">{t('description')}</p>
        <h2 className="notification-preferences__subtitle">{t('push.label')}</h2>
        <PushDevicePreferenceControl t={t} />
        <div className="notification-preferences__sections">
          {preferenceSections.map((section) => (
            <NotificationPreferenceSection
              key={section.id}
              enabled={areAllSectionEventsEnabled(preferences, section)}
              essential={section.essential === true}
              events={section.events}
              id={section.id}
              onEventChange={(eventId, nextValue) => handleEventChange(section, eventId, nextValue)}
              onSectionChange={(nextValue) => handleSectionChange(section, nextValue)}
              preferences={preferences[section.id]}
              saving={Boolean(savingKey)}
              t={t}
              title={t(`sections.${section.id}.title`)}
            />
          ))}
        </div>
        {saveError && <p className="notification-preferences__save-error" role="alert">{saveError}</p>}
        <p className="notification-preferences__essential-notice">{t('essential_notice')}</p>
      </div>
    </>
  );
}
